import { usePlaylistEngine } from '../hooks/usePlaylistEngine';
import { useMediaQueue } from '../hooks/useMediaQueue';
import type { Channel } from '../types/Channel';
import type { PlaylistItem } from '../types/PlaylistItem';
import PlayerHeader from './PlayerHeader';
import PlayerCanvas from './PlayerCanvas';
import PlayerFooter from './PlayerFooter';

interface ChannelPlayerProps {
  channel: Channel;
  playlist: PlaylistItem[];
}

export default function ChannelPlayer({ channel, playlist }: ChannelPlayerProps) {
  const engine = usePlaylistEngine(playlist);
  const media = useMediaQueue(engine.state.playlist, engine.state.currentIndex);

  const currentTrack = engine.state.currentItem;
  const isPlaying = true;
  const duration = currentTrack?.duration_seconds ?? currentTrack?.duration ?? 0;
  const totalItems = engine.state.playlist.length;
  const displayIndex = engine.state.isEmpty ? 0 : engine.state.currentIndex + 1;

  return (
    <div className="flex flex-col h-screen bg-neutral-950 overflow-hidden">
      <PlayerHeader channel={channel} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex">
          <PlayerCanvas
            playbackMode={channel.playbackMode}
            currentMedia={media.currentMedia}
          />

          <div className="w-72 border-l border-white/5 bg-neutral-900/50 p-5 flex flex-col gap-5 overflow-y-auto">
            <div>
              <h2 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">
                Playlist Engine
              </h2>

              {engine.state.isEmpty ? (
                <div className="text-neutral-500 text-sm py-4 text-center">
                  <i className="ri-play-list-2-line text-2xl block mb-2 opacity-40" />
                  No items in playlist
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-neutral-800/60 rounded-lg p-3">
                    <div className="text-neutral-400 text-[10px] uppercase tracking-wider font-medium mb-1">
                      Current
                    </div>
                    <div className="text-white text-sm font-medium truncate">
                      {engine.state.currentItem?.title ?? '—'}
                    </div>
                  </div>

                  <div className="bg-neutral-800/60 rounded-lg p-3">
                    <div className="text-neutral-400 text-[10px] uppercase tracking-wider font-medium mb-1">
                      Type
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-medium capitalize">
                        {engine.state.currentItem?.type ?? '—'}
                      </span>
                      {engine.state.currentItem?.type && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/20 text-emerald-400">
                          {engine.state.currentItem.type}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="bg-neutral-800/60 rounded-lg p-3">
                    <div className="text-neutral-400 text-[10px] uppercase tracking-wider font-medium mb-1">
                      Index
                    </div>
                    <div className="text-white text-sm font-mono font-semibold">
                      {displayIndex} / {totalItems}
                    </div>
                  </div>

                  <div className="bg-neutral-800/60 rounded-lg p-3">
                    <div className="text-neutral-400 text-[10px] uppercase tracking-wider font-medium mb-1">
                      Artist
                    </div>
                    <div className="text-white text-sm truncate">
                      {engine.state.currentItem?.artist ?? '—'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <h2 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">
                Media Queue
              </h2>

              {media.currentMedia ? (
                <div className="space-y-3">
                  <div className="bg-neutral-800/60 rounded-lg p-3">
                    <div className="text-neutral-400 text-[10px] uppercase tracking-wider font-medium mb-1">
                      Current media
                    </div>
                    <div className="text-white text-sm font-medium truncate">
                      {media.currentMedia.title}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 capitalize">
                        {media.currentMedia.type}
                      </span>
                      <span className="text-neutral-500 text-[10px]">
                        {media.currentMedia.duration_seconds > 0
                          ? `${media.currentMedia.duration_seconds}s`
                          : `${media.currentMedia.display_duration_seconds}s display`}
                      </span>
                    </div>
                  </div>

                  <div className="bg-neutral-800/60 rounded-lg p-3">
                    <div className="text-neutral-400 text-[10px] uppercase tracking-wider font-medium mb-1">
                      Next media
                    </div>
                    {media.nextMedia ? (
                      <div className="text-white text-sm truncate">
                        {media.nextMedia.title}
                        <span className="text-neutral-500 text-[10px] ml-2 capitalize">
                          ({media.nextMedia.type})
                        </span>
                      </div>
                    ) : (
                      <div className="text-neutral-500 text-sm">—</div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-emerald-500/10 rounded-lg p-3 text-center border border-emerald-500/20">
                      <div className="text-emerald-400 text-lg font-bold font-mono">
                        {media.audioItems.length}
                      </div>
                      <div className="text-emerald-400/60 text-[10px] uppercase tracking-wider mt-0.5">
                        Audio
                      </div>
                    </div>

                    <div className="bg-amber-500/10 rounded-lg p-3 text-center border border-amber-500/20">
                      <div className="text-amber-400 text-lg font-bold font-mono">
                        {media.visualItems.length}
                      </div>
                      <div className="text-amber-400/60 text-[10px] uppercase tracking-wider mt-0.5">
                        Visual
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-neutral-500 text-sm py-4 text-center">
                  <i className="ri-stack-line text-2xl block mb-2 opacity-40" />
                  No media in queue
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                Controls
              </h3>

              <button
                type="button"
                onClick={engine.previous}
                disabled={engine.state.isEmpty}
                className="w-full px-4 py-2.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <i className="ri-skip-back-fill text-base" />
                Previous
              </button>

              <button
                type="button"
                onClick={engine.next}
                disabled={engine.state.isEmpty}
                className="w-full px-4 py-2.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                Next
                <i className="ri-skip-forward-fill text-base" />
              </button>

              <button
                type="button"
                onClick={engine.reset}
                disabled={engine.state.isEmpty}
                className="w-full px-4 py-2.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <i className="ri-restart-line text-base" />
                Reset
              </button>
            </div>

            {engine.state.playlist.length > 0 && (
              <div className="flex-1 min-h-0">
                <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 sticky top-0 bg-neutral-900/50 py-1">
                  Queue ({engine.state.playlist.length})
                </h3>
                <div className="space-y-1">
                  {engine.state.playlist.map((item, idx) => (
                    <div
                      key={item.id}
                      className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                        idx === engine.state.currentIndex
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : 'text-neutral-400 hover:text-white hover:bg-neutral-800/60'
                      }`}
                    >
                      <span className="text-[10px] font-mono text-neutral-600 w-5 shrink-0">
                        {idx + 1}
                      </span>
                      <span className="truncate flex-1">{item.title}</span>
                      <span className="text-[10px] uppercase text-neutral-600 shrink-0">
                        {item.type}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <PlayerFooter
          currentTrack={currentTrack}
          isPlaying={isPlaying}
          currentTime={0}
          duration={duration}
        />
      </div>
    </div>
  );
}